import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Tag, 
  Spin, 
  notification,
  Row,
  Col,
  Divider,
  Image
} from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { newsService } from '../../services';

const { Title, Paragraph } = Typography;

const NewsView = () => {
  const [loading, setLoading] = useState(true);
  const [newsData, setNewsData] = useState(null);
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const navigate = useNavigate();
  const { id } = useParams();

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadNewsDetail();
  }, [id]);

  const loadNewsDetail = async () => {
    try {
      setLoading(true);
      const response = await newsService.getById(id);
      
      if (response.success && response.data) {
        setNewsData(response.data);
      } else {
        throw new Error('Không tìm thấy tin tức');
      }
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải thông tin tin tức'
      });
      navigate('/news/list');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/news/list');
  };

  const handleEdit = () => {
    navigate(`/news/edit/${id}`);
  };

  if (loading) {
    return (
      <div style={{ padding: isMobile ? 8 : 24, textAlign: 'center' }}>
        <Spin size="large" />
        {isMobile && (
          <div style={{ marginTop: 16, color: '#666' }}>Đang tải...</div>
        )}
      </div>
    );
  }

  if (!newsData) {
    return (
      <div style={{ padding: isMobile ? 8 : 24 }}>
        <Card>
          <Title level={isMobile ? 5 : 4}>Không tìm thấy tin tức</Title>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 8 : 24 }}>
      <Card>
        <div style={{ marginBottom: isMobile ? 16 : 24 }}>
          <Space size={isMobile ? 'small' : 'middle'}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBack}
              size={isMobile ? 'middle' : 'default'}
            >
              Quay lại
            </Button>
            <Button 
              type="primary"
              icon={<EditOutlined />} 
              onClick={handleEdit}
              size={isMobile ? 'middle' : 'default'}
            >
              Chỉnh sửa
            </Button>
          </Space>
        </div>

        {isMobile ? (
          // Mobile Layout: Single column, stacked content
          <div>
            <Title level={3} style={{ marginBottom: 16 }}>
              {newsData.title}
            </Title>
            
            <div style={{ marginBottom: 16 }}>
              <Space wrap size="small">
                {newsData.topic_name && (
                  <Tag color="blue" size="small">{newsData.topic_name}</Tag>
                )}
                {newsData.category_name && (
                  <Tag color="green" size="small">{newsData.category_name}</Tag>
                )}
                {newsData.is_featured === 1 && (
                  <Tag color="gold" size="small">Nổi bật</Tag>
                )}
                {newsData.is_new === 1 && (
                  <Tag color="red" size="small">Mới</Tag>
                )}
              </Space>
            </div>

            {/* Mobile Info Card */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
              <Row gutter={[8, 8]} style={{ fontSize: '12px' }}>
                <Col span={12}>
                  <strong>ID:</strong> #{newsData.id}
                </Col>
                <Col span={12}>
                  <strong>Chủ đề:</strong> {newsData.topic_name || 'Chưa có'}
                </Col>
                <Col span={12}>
                  <strong>Danh mục:</strong> {newsData.category_name || 'Chưa có'}
                </Col>
                <Col span={12}>
                  <strong>Tạo:</strong> {newsData.created_at ? new Date(newsData.created_at).toLocaleDateString('vi-VN') : 'N/A'}
                </Col>
              </Row>
            </Card>

            {newsData.summary && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Title level={5} style={{ marginBottom: 8 }}>Tóm tắt:</Title>
                <Paragraph style={{ fontSize: 14, fontStyle: 'italic', margin: 0 }}>
                  {newsData.summary}
                </Paragraph>
              </Card>
            )}

            {newsData.cover_image && (
              <div style={{ marginBottom: 16, textAlign: 'center' }}>
                <Image
                  src={newsData.cover_image}
                  alt={newsData.title}
                  style={{ 
                    width: '100%', 
                    maxHeight: 250,
                    borderRadius: 8,
                    objectFit: 'cover'
                  }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxY4Q9ICU="
                />
              </div>
            )}

            <Card>
              <Title level={5} style={{ marginBottom: 12 }}>Nội dung:</Title>
              <div 
                style={{ 
                  lineHeight: 1.6, 
                  fontSize: 14,
                  padding: 12,
                  backgroundColor: '#fafafa',
                  borderRadius: 6,
                  wordBreak: 'break-word',
                  overflow: 'hidden'
                }}
                dangerouslySetInnerHTML={{ __html: newsData.content_html || 'Không có nội dung' }}
              />
            </Card>
          </div>
        ) : (
          // Desktop Layout: Two columns
          <Row gutter={24}>
          <Col span={16}>
            <Title level={2}>{newsData.title}</Title>
            
            <div style={{ marginBottom: 16 }}>
              <Space wrap>
                {newsData.topic_name && (
                  <Tag color="blue">{newsData.topic_name}</Tag>
                )}
                {newsData.category_name && (
                  <Tag color="green">{newsData.category_name}</Tag>
                )}
                {newsData.is_featured === 1 && (
                  <Tag color="gold">Nổi bật</Tag>
                )}
                {newsData.is_new === 1 && (
                  <Tag color="red">Mới</Tag>
                )}
              </Space>
            </div>

            {newsData.summary && (
              <>
                <Title level={4}>Tóm tắt:</Title>
                <Paragraph style={{ fontSize: 16, fontStyle: 'italic' }}>
                  {newsData.summary}
                </Paragraph>
                <Divider />
              </>
            )}

            {newsData.cover_image && (
              <div style={{ marginBottom: 16, textAlign: 'center' }}>
                <Image
                  src={newsData.cover_image}
                  alt={newsData.title}
                  style={{ maxWidth: '100%', maxHeight: 400 }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxY4Q9ICQ=="
                />
              </div>
            )}

            <Title level={4}>Nội dung:</Title>
            <div 
              style={{ 
                lineHeight: 1.8, 
                fontSize: 16,
                border: '1px solid #f0f0f0',
                padding: 16,
                borderRadius: 6,
                backgroundColor: '#fafafa'
              }}
              dangerouslySetInnerHTML={{ __html: newsData.content_html || 'Không có nội dung' }}
            />
          </Col>

          <Col span={8}>
            <Card title="Thông tin tin tức" size="small">
              <div style={{ marginBottom: 12 }}>
                <strong>ID:</strong> {newsData.id}
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>Chủ đề:</strong> {newsData.topic_name || 'Chưa có'}
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>Danh mục:</strong> {newsData.category_name || 'Chưa có'}
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>Trạng thái:</strong>
                <div style={{ marginTop: 4 }}>
                  {newsData.is_featured === 1 && (
                    <Tag color="gold">Nổi bật</Tag>
                  )}
                  {newsData.is_new === 1 && (
                    <Tag color="red">Mới</Tag>
                  )}
                  {newsData.is_featured !== 1 && newsData.is_new !== 1 && (
                    <Tag>Thường</Tag>
                  )}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>Ngày tạo:</strong> 
                <br />
                {newsData.created_at ? new Date(newsData.created_at).toLocaleString('vi-VN') : 'Không có'}
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>Ngày cập nhật:</strong>
                <br />
                {newsData.updated_at ? new Date(newsData.updated_at).toLocaleString('vi-VN') : 'Chưa cập nhật'}
              </div>
            </Card>
          </Col>
        </Row>
        )}
      </Card>
    </div>
  );
};

export default NewsView;
